import React, { useEffect, useState, useRef } from "react";
import {
  StyleSheet,
  ScrollView,
  Pressable,
  Text,
  TextInput,
  ActivityIndicator,
  useColorScheme,
} from "react-native";
import Alert from "../components/Alert";
import { View } from "../components/Themed";
import { useQuery, gql, useMutation } from "@apollo/client";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { MaterialIcons } from "@expo/vector-icons";
import { v4 as uuidv4 } from "uuid";
import localization from "../utils/localization";

const CATEGORIES_QUERY = gql`
  query getCategories {
    categories(token: "token") {
      id
      name
      order
    }
  }
`;

const CREATE_CATEGORY_MUTATION = gql`
  mutation createCategory($token: String!, $operator: ID!, $id: ID!, $name: String!, $order: Int!) {
    createCategory(token: $token, operator: $operator, id: $id, name: $name, order: $order) {
      id
      name
      order
    }
  }
`;

const UPDATE_CATEGORY_MUTATION = gql`
  mutation updateCategory($token: String!, $operator: ID!, $id: ID!, $name: String, $order: Int) {
    updateCategory(token: $token, operator: $operator, id: $id, name: $name, order: $order) {
      id
      name
      order
    }
  }
`;

const DELETE_CATEGORY_MUTATION = gql`
  mutation deleteCategory($token: String!, $operator: ID!, $id: ID!) {
    deleteCategory(token: $token, operator: $operator, id: $id)
  }
`;

export default function OperatorCategoriesScreen() {
  const [token, setToken] = useState("");
  const [adminToken, setAdminToken] = useState("");
  const [operatorId, setOperatorId] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryId, setNewCategoryId] = useState("");
  const [newCategoryOrder, setNewCategoryOrder] = useState("");
  const colorScheme = useColorScheme();
  const scrollViewRef = useRef(null);

  const { loading, error, data, refetch } = useQuery(CATEGORIES_QUERY);

  useEffect(() => {
    async function fetchData() {
      try {
        const storedToken = await AsyncStorage.getItem("token");
        setToken(storedToken);
      } catch (ex) {}

      try {
        const storedAdminToken = await AsyncStorage.getItem("adminToken");
        setAdminToken(storedAdminToken);
      } catch (ex) {}

      try {
        const storedId = await AsyncStorage.getItem("operatorId");
        setOperatorId(storedId);
      } catch (ex) {}
    }
    fetchData();
  }, []);

  const [createCategory] = useMutation(CREATE_CATEGORY_MUTATION);
  const [updateCategory] = useMutation(UPDATE_CATEGORY_MUTATION);
  const [deleteCategory] = useMutation(DELETE_CATEGORY_MUTATION);

  const resetForm = () => {
    setNewCategoryId("");
    setNewCategoryName("");
    setNewCategoryOrder("");
    setShowCreateForm(false);
    setEditingCategory(null);
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      Alert.alert(localization.t("Error"), localization.t("Please enter a category name"));
      return;
    }

    const categoryId = uuidv4();

    try {
      const nextOrder = newCategoryOrder ? parseInt(newCategoryOrder) : (data?.categories?.length || 0);
      await createCategory({
        variables: {
          token: adminToken,
          operator: operatorId,
          id: categoryId,
          name: newCategoryName.trim(),
          order: nextOrder,
        },
      });
      resetForm();
      refetch();
      Alert.alert(localization.t("Success"), localization.t("Category created successfully"));
    } catch (error) {
      Alert.alert(localization.t("Error"), error.message || localization.t("Failed to create category"));
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory) return;

    try {
      const updateData = {};
      if (newCategoryName.trim()) updateData.name = newCategoryName.trim();
      if (newCategoryOrder.trim()) updateData.order = parseInt(newCategoryOrder);

      await updateCategory({
        variables: {
          token: adminToken,
          operator: operatorId,
          id: editingCategory.id,
          ...updateData,
        },
      });
      resetForm();
      refetch();
      Alert.alert(localization.t("Success"), localization.t("Category updated successfully"));
    } catch (error) {
      Alert.alert(localization.t("Error"), error.message || localization.t("Failed to update category"));
    }
  };

  const handleDeleteCategory = async (id, name) => {
    Alert.alert(
      localization.t("Delete Category"),
      `${localization.t("Are you sure you want to delete")} "${name}"?`,
      [
        { text: localization.t("Cancel"), style: "cancel" },
        {
          text: localization.t("Delete"),
          style: "destructive",
          onPress: async () => {
            try {
              await deleteCategory({
                variables: {
                  token: adminToken,
                  operator: operatorId,
                  id,
                },
              });
              refetch();
              Alert.alert(localization.t("Success"), localization.t("Category deleted successfully"));
            } catch (error) {
              Alert.alert(localization.t("Error"), error.message || localization.t("Failed to delete category"));
            }
          },
        },
      ]
    );
  };

  const startEditing = (category) => {
    setEditingCategory(category);
    setNewCategoryName(category.name);
    setNewCategoryOrder(category.order.toString());
    setShowCreateForm(true);
    setTimeout(() => {
      scrollViewRef.current?.scrollTo({ y: 0, animated: true });
    }, 100);
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>{localization.t("Error loading categories")}: {error.message}</Text>
      </View>
    );
  }

  const categories = data?.categories || [];
  const sortedCategories = [...categories].sort((a, b) => a.order - b.order);

  return (
    <ScrollView ref={scrollViewRef} style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Categories Management</Text>
        <Pressable
          style={styles.createButton}
          onPress={() => {
            resetForm();
            setShowCreateForm(!showCreateForm);
          }}
        >
          <MaterialIcons name="add" size={24} color="white" />
          <Text style={styles.buttonText}>{showCreateForm ? "Cancel" : "New Category"}</Text>
        </Pressable>
      </View>

      {showCreateForm && (
        <View style={styles.formSection}>
          <Text style={styles.sectionTitle}>
            {editingCategory ? "Edit Category" : "Create New Category"}
          </Text>

          <TextInput
            style={styles.input}
            placeholder="Category Name *"
            placeholderTextColor={colorScheme === "dark" ? "#999" : "#666"}
            value={newCategoryName}
            onChangeText={setNewCategoryName}
          />

          <TextInput
            style={styles.input}
            placeholder="Order (optional)"
            placeholderTextColor={colorScheme === "dark" ? "#999" : "#666"}
            value={newCategoryOrder}
            onChangeText={setNewCategoryOrder}
            keyboardType="numeric"
          />

          <Pressable
            style={styles.submitButton}
            onPress={editingCategory ? handleUpdateCategory : handleCreateCategory}
          >
            <Text style={styles.buttonText}>
              {editingCategory ? "Update Category" : "Create Category"}
            </Text>
          </Pressable>
        </View>
      )}

      <View style={styles.categoriesSection}>
        <Text style={styles.sectionTitle}>Categories ({categories.length})</Text>
        {sortedCategories.map((category) => (
          <View key={category.id} style={styles.categoryItem}>
            <View style={styles.categoryHeader}>
              <View style={styles.categoryInfo}>
                <Text style={styles.categoryName}>{category.name}</Text>
                <Text style={styles.categoryMeta}>
                  ID: {category.id} • Order: {category.order}
                </Text>
              </View>
              <View style={styles.actionButtons}>
                <Pressable
                  style={styles.iconButton}
                  onPress={() => startEditing(category)}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <MaterialIcons name="edit" size={24} color="#007AFF" />
                </Pressable>
                <Pressable
                  style={styles.iconButton}
                  onPress={() => handleDeleteCategory(category.id, category.name)}
                >
                  <MaterialIcons name="delete" size={24} color="#FF3B30" />
                </Pressable>
              </View>
            </View>
          </View>
        ))}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 15,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
  },
  createButton: {
    backgroundColor: "#007AFF",
    flexDirection: "row",
    alignItems: "center",
    padding: 10,
    borderRadius: 8,
    gap: 5,
  },
  buttonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
  formSection: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 8,
    padding: 15,
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 15,
  },
  input: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
    fontSize: 16,
  },
  submitButton: {
    backgroundColor: "#34C759",
    padding: 15,
    borderRadius: 8,
    alignItems: "center",
  },
  categoriesSection: {
    marginBottom: 20,
  },
  categoryItem: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 8,
    padding: 15,
    marginBottom: 15,
  },
  categoryHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  categoryInfo: {
    flex: 1,
  },
  categoryName: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 5,
  },
  categoryMeta: {
    fontSize: 12,
    color: "#999",
  },
  actionButtons: {
    flexDirection: "row",
    gap: 10,
  },
  iconButton: {
    padding: 8,
  },
  errorText: {
    color: "red",
    fontSize: 16,
    textAlign: "center",
  },
});
